/**
 * Assets: every file the business keeps, at /assets (and /assets/<folder>). The same library the control every
 * upload opens, page-sized: kinds, sort, a file's inspector with where it is used, Move, Download, Delete with Undo,
 * and "Make a room from this" for a talk. A computer draws the page below; a phone and an iPad draw SeasonAssets
 * from the same hooks.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import type { LibraryAsset, LibraryFolder } from "../../shared/types";
import { FolderMenu } from "../assets/FolderActions";
import { ALL, FITS, type KindFilter, type ListContext, SORTS, type SortName, countUses, downloadHref, folderName, folderPath, kindName, listFiles, uploadTarget, usedWords } from "../assets/library";
import { ByLine, Facts, Rail, RefRow, Results, SearchBox, Stage, TrashResults } from "../assets/parts";
import { createFolder, loadLibrary, uploadFiles, uploadsAsAssets } from "../assets/store";
import { SeasonAssets } from "../assets/SeasonAssets";
import { useAssetsData, useFileActions, useFollowFolder } from "../assets/useAssets";
import { useLayout } from "../components/season/layout";
import { Icon } from "../components/ui/Icon";
import { useToast } from "../components/ui/Toast";

const KINDS: [KindFilter, string, Parameters<typeof Icon>[0]["name"] | null][] = [
  ["all", "Everything", null],
  ["video", "Videos", "video"],
  ["image", "Pictures", "pic"],
  ["doc", "PDFs and text", "file"],
  ["sound", "Sounds", "music"],
];

function SortMenu({ sort, onSort }: { sort: SortName; onSort: (s: SortName) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  return (
    <div className="as-sortw" ref={box}>
      <button type="button" className="chip as-sort" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon name="sortv" />
        {SORTS[sort]}
        <Icon name="down" />
      </button>
      {open ? (
        <div className="menu" role="menu">
          {(Object.keys(SORTS) as SortName[]).map((k) => (
            <button
              type="button"
              role="menuitemradio"
              aria-checked={sort === k}
              key={k}
              onClick={() => {
                onSort(k);
                setOpen(false);
              }}
            >
              {sort === k ? <Icon name="check" /> : <svg className="i" aria-hidden="true" />} {SORTS[k]}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MoveMenu({ a, folders, onMove }: { a: LibraryAsset; folders: LibraryFolder[]; onMove: (folderId: string | null) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const opts = folders.filter((f) => !f.system && f.id !== a.folder_id);
  return (
    <div className="as-movew" ref={box}>
      <button type="button" className="btn" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
        Move
      </button>
      {open ? (
        <div className="menu" role="menu">
          <div className="mh small muted">Move to</div>
          {a.folder_id ? (
            <button type="button" role="menuitem" onClick={() => onMove(null)}>
              <Icon name="allf" /> Assets, in no folder
            </button>
          ) : null}
          {opts.map((f) => (
            <button type="button" role="menuitem" key={f.id} onClick={() => onMove(f.id)}>
              <Icon name="fold" /> {f.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ComputerAssets() {
  const { lib, flashkut, blind, canChange, folders, shown, trashed } = useAssetsData();
  const toast = useToast();
  const navigate = useNavigate();
  const actions = useFileActions(folders);
  const making = actions.making;
  const { folder: routeFolder } = useParams();
  const [params] = useSearchParams();
  const inTrash = params.get("view") === "trash";
  const folder = routeFolder && folders.some((f) => f.id === routeFolder) ? routeFolder : ALL;
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<KindFilter>("all");
  const [sort, setSort] = useState<SortName>("new");
  const [sel, setSel] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [newFolder, setNewFolder] = useState(false);
  const [drop, setDrop] = useState<number | null>(null);
  const depth = useRef(0);
  const file = useRef<HTMLInputElement>(null);
  const search = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void loadLibrary();
  }, []);

  // Why no folder in Trash: none of the folders is the one open, and a folder's own id is never "".
  const ctx: ListContext = { mode: "page", folder: inTrash ? "" : folder, q, kind, sort, fit: FITS.any };
  const pending = lib.uploads.filter((u) => u.from === "page");
  const all = [...uploadsAsAssets(pending), ...shown];
  const first = new Set([...pending.map((u) => u.id), ...lib.fresh]);
  const files = listFiles(folders, all, ctx, first);
  const a = shown.find((x) => x.id === sel) ?? null;
  const target = uploadTarget(folders, folder);

  const open = (id: string) => {
    setQ("");
    setNewFolder(false);
    setConfirm(null);
    setTicked(new Set());
    navigate(id === ALL ? "/assets" : `/assets/${encodeURIComponent(id)}`, { replace: true });
  };
  const openTrash = () => {
    setSel(null);
    setConfirm(null);
    setNewFolder(false);
    setQ("");
    setTicked(new Set());
    navigate("/assets?view=trash", { replace: true });
  };
  const tick = (id: string) => setTicked((was) => (was.has(id) ? new Set([...was].filter((x) => x !== id)) : new Set([...was, id])));
  const restore = async () => {
    const ids = [...ticked];
    setTicked(new Set());
    await actions.restore(ids);
  };
  // Why it closes the file: its name form opens in the list, which a file's sheet would cover on a phone.
  const startFolder = () => {
    setSel(null);
    setConfirm(null);
    setNewFolder(true);
    setQ("");
  };
  const upload = (list: File[]) => {
    if (!list.length || blind) return;
    if (q || (folder !== ALL && folder !== target)) open(target ?? ALL);
    const again = uploadFiles(list, target, "page", (done) => {
      setSel(done.id);
      toast(`Ready in ${folderName(folders, done.folder_id)}.`);
    });
    for (const name of again) toast(`${name} is already going up.`);
  };

  // Drop anywhere on the page: the path it lands in is shown while dragging.
  useEffect(() => {
    const isFiles = (e: DragEvent) => [...(e.dataTransfer?.types ?? [])].includes("Files");
    const enter = (e: DragEvent) => {
      if (!isFiles(e) || document.getElementById("as-layer")) return;
      e.preventDefault();
      depth.current += 1;
      setDrop(e.dataTransfer?.items?.length ?? 0);
    };
    const over = (e: DragEvent) => {
      if (isFiles(e) && !document.getElementById("as-layer")) e.preventDefault();
    };
    const leave = (e: DragEvent) => {
      if (!isFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (!depth.current) setDrop(null);
    };
    const dropped = (e: DragEvent) => {
      if (!isFiles(e) || document.getElementById("as-layer")) return;
      e.preventDefault();
      depth.current = 0;
      setDrop(null);
      upload([...(e.dataTransfer?.files ?? [])]);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragover", over);
    window.addEventListener("dragleave", leave);
    window.addEventListener("drop", dropped);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragover", over);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("drop", dropped);
    };
  });

  const remove = async (x: LibraryAsset) => {
    setSel(null);
    setConfirm(null);
    await actions.remove(x);
  };

  const follow = useFollowFolder(folder, "/assets");
  const folderChanged = (from: string, to: string | null) => {
    setSel(null);
    follow(from, to);
  };
  const folderMenu = canChange
    ? (f: LibraryFolder) =>
        f.system ? null : <FolderMenu key={f.id} folder={f} folders={folders} assets={shown} onChanged={(to) => folderChanged(f.id, to)} />
    : undefined;

  const inspector = (x: LibraryAsset) => {
    const own = x.source === "upload" && canChange;
    const counts = countUses(x.uses);
    const n = counts.rooms + counts.emails + counts.chats + counts.pages + (counts.all ? 1 : 0);
    const talk = FITS.talk.test(x) && (x.source !== "flashkut" || flashkut);
    return (
      <>
        <div className="as-insp-scrim" onClick={() => setSel(null)} aria-hidden="true" />
        <aside className="as-insp card" aria-label={x.name}>
          <button type="button" className="ibtn sm as-insp-x" aria-label="Close" onClick={() => setSel(null)}>
            <Icon name="x" />
          </button>
          <Stage a={x} big />
          <div className="as-det">
            <h3>{x.name}</h3>
            <div className="as-kl">
              {kindName(x)}
              {x.source === "room" && x.webinar_id ? " · Recorded live in Flashroom" : ""}
            </div>
            <ByLine a={x} fresh={lib.fresh.includes(x.id)} />
            <div className="as-actw">
              {confirm === x.id ? (
                <div className="as-confirm" role="alert">
                  <b>Delete <bdi>{x.name}</bdi>?</b>
                  <span>It stays in Trash for 30 days.</span>
                  {n ? <span>{usedWords(x.uses, "it")}</span> : null}
                  <div className="row g8">
                    <button type="button" className="btn s as-bad" onClick={() => void remove(x)}>
                      Delete
                    </button>
                    <button type="button" className="btn s q" onClick={() => setConfirm(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {talk ? (
                    <button type="button" className="btn p full" disabled={making === x.id} onClick={() => void actions.makeRoom(x)}>
                      {making === x.id ? <span className="spin" aria-hidden="true" /> : <Icon name="plus" />} Make a room from this
                    </button>
                  ) : null}
                  <div className="as-acts">
                    {x.source === "room" && x.webinar_id ? (
                      <button type="button" className="btn" onClick={() => navigate(`/rooms/${x.webinar_id}`)}>
                        <Icon name="play" /> Open its room
                      </button>
                    ) : null}
                    {downloadHref(x) && x.ready ? (
                      <a className="btn" href={downloadHref(x) ?? undefined} download={x.name}>
                        Download
                      </a>
                    ) : null}
                    {own ? (
                      <>
                        <MoveMenu
                          a={x}
                          folders={folders}
                          onMove={(to) => {
                            void actions.move(x, to);
                          }}
                        />
                        <button type="button" className="btn as-badq" onClick={() => setConfirm(x.id)}>
                          Delete
                        </button>
                      </>
                    ) : null}
                  </div>
                </>
              )}
            </div>
            <Facts a={x} folders={folders} />
            <div className="as-used">
              <div className="as-k">{x.source === "room" ? (x.webinar_id ? "Where it plays" : "Where it’s from") : "Where it’s used"}</div>
              {x.uses.length ? x.uses.map((u, i) => <RefRow key={`${u.kind}-${u.ref}-${i}`} u={u} onGo={(href) => navigate(href)} />) : <p className="as-none">Not used yet</p>}
            </div>
          </div>
        </aside>
      </>
    );
  };

  return (
    <div className="page wide asp">
      <div className="ph">
        <div>
          <h1>Assets</h1>
        </div>
        <div className="row g8">
          {canChange ? (
            <>
              <button type="button" className="btn" disabled={blind} onClick={startFolder}>
                <Icon name="foldplus" /> New folder
              </button>
              <button type="button" className="btn p" disabled={blind} onClick={() => file.current?.click()}>
                <Icon name="upload" /> Upload
              </button>
            </>
          ) : null}
        </div>
      </div>
      <div id="as-page">
        <div className={`as-body${a ? " has-insp" : ""}`}>
          <Rail
            ctx={ctx}
            folders={folders}
            assets={all}
            onOpen={open}
            onNewFolder={startFolder}
            canAdd={canChange}
            blind={blind}
            trash={{ count: trashed.length, on: inTrash, onOpen: openTrash }}
          />
          <section className="as-main" aria-label="Files" id="as-main-pg">
            <div className="as-tools">
              <SearchBox id="as-q-pg" value={q} placeholder="Search every file and folder" inputRef={search} onChange={(v) => {
                  setQ(v);
                  if (inTrash && v) navigate("/assets", { replace: true });
                }} />
              {inTrash ? null : (
                <div className="seg" role="group" aria-label="Kind">
                  {KINDS.map(([k, label, icon]) => (
                    <button type="button" key={k} className={kind === k ? "on" : ""} aria-pressed={kind === k} onClick={() => setKind(k)}>
                      {icon ? <Icon name={icon} /> : null}
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div id="as-res-pg">
              {lib.status === "loading" && !shown.length ? (
                <div className="as-empty">
                  <span className="spin" />
                </div>
              ) : lib.status === "error" ? (
                <div className="as-empty">
                  <Icon name="info" />
                  <b>{lib.error}</b>
                  <button type="button" className="btn s" onClick={() => void loadLibrary(true)}>
                    Try again
                  </button>
                </div>
              ) : inTrash ? (
                <>
                  {ticked.size && canChange ? (
                    <div className="as-tbar" role="toolbar" aria-label="Selected files">
                      <b className="num">{ticked.size} selected</b>
                      <button type="button" className="btn p s" onClick={() => void restore()}>
                        Restore
                      </button>
                      <button type="button" className="ibtn sm" aria-label="Clear selection" onClick={() => setTicked(new Set())}>
                        <Icon name="x" />
                      </button>
                    </div>
                  ) : null}
                  <TrashResults ctx={ctx} folders={folders} files={trashed} ticked={ticked} ticks={canChange} onTick={tick} />
                </>
              ) : (
                <Results
                  ctx={ctx}
                  folders={folders}
                  assets={all}
                  files={files}
                  uploads={pending}
                  sel={sel}
                  auto={null}
                  fresh={lib.fresh}
                  height={164}
                  newFolder={newFolder}
                  newFolderWhere={folderName(folders, target)}
                  sortControl={<SortMenu sort={sort} onSort={setSort} />}
                  folderMenu={folderMenu}
                  canAdd={canChange}
                  onSelect={(id) => {
                    setConfirm(null);
                    setSel(sel === id ? null : id);
                  }}
                  onOpen={open}
                  onToggleAll={() => {}}
                  onClear={() => {
                    setQ("");
                    search.current?.focus();
                  }}
                  onUpload={() => file.current?.click()}
                  onCreateFolder={(name) => {
                    void createFolder(name, target).then(
                      (f) => {
                        setNewFolder(false);
                        setSel(null);
                        open(f.id);
                      },
                      (e: unknown) => toast({ text: e instanceof Error ? e.message : "That folder wasn’t made.", error: true }),
                    );
                  }}
                  onCancelFolder={() => setNewFolder(false)}
                />
              )}
            </div>
          </section>
          {a ? inspector(a) : null}
        </div>
      </div>
      {drop !== null ? (
        <div className="as-drop pg">
          <div className="as-drop-in">
            <span className="as-drop-ic">
              <Icon name="upload" />
            </span>
            <b>Drop to add to</b>
            <span className="as-drop-p">
              {["Assets", ...folderPath(folders, target).map((f) => f.name)].map((name, i) => (
                <Fragment key={`${name}-${i}`}>
                  {i ? <Icon name="chev" /> : null}
                  <span>{name}</span>
                </Fragment>
              ))}
            </span>
            {drop ? (
              <small>
                {drop} file{drop === 1 ? "" : "s"}
              </small>
            ) : null}
          </div>
        </div>
      ) : null}
      <input
        ref={file}
        type="file"
        hidden
        multiple
        onChange={(e) => {
          const list = [...(e.target.files ?? [])];
          e.target.value = "";
          upload(list);
        }}
      />
    </div>
  );
}

/** Assets: the computer's page on a computer, the Season page on a phone and an iPad. */
export function AssetsView() {
  const layout = useLayout();
  return layout === "computer" ? <ComputerAssets /> : <SeasonAssets layout={layout} />;
}
